import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FaLinkedin, FaGithub, FaEnvelope, FaPhone } from 'react-icons/fa';

const About = () => {
  const timeline = [
    {
      year: '2019',
      title: 'Premiers pas dans le développement',
      description: 'Formation autodidacte intensive en développement web. Premiers projets personnels et freelance.'
    },
    {
      year: '2020',
      title: 'Spécialisation React & Node.js',
      description: 'Approfondissement des technologies JavaScript modernes. Premiers clients professionnels.'
    },
    {
      year: '2021',
      title: 'Extension vers le Cloud',
      description: 'Certifications AWS. Premiers projets d\'infrastructure cloud pour PME.'
    },
    {
      year: '2022',
      title: 'Lancement d\'Olney Tech Solutions',
      description: 'Création de l\'entreprise. Focus sur les solutions digitales complètes pour les entreprises locales.'
    },
    {
      year: '2023',
      title: 'Expertise IA & Automatisation',
      description: 'Intégration des technologies IA (Claude, GPT) dans nos solutions. Premiers chatbots intelligents.'
    },
    {
      year: '2024-2025',
      title: 'Croissance & Innovation',
      description: '150+ projets réalisés. Expansion de l\'équipe. Nouvelles offres en cybersécurité et apps mobiles.'
    }
  ];

  const values = [
    {
      icon: '🎯',
      title: 'Excellence',
      description: 'Chaque projet est traité avec le plus grand soin. Nous ne livrons que des solutions dont nous sommes fiers.'
    },
    {
      icon: '🤝',
      title: 'Proximité',
      description: 'Basés dans le Nord, nous privilégions la relation humaine. Un interlocuteur unique, disponible et réactif.'
    },
    {
      icon: '💡',
      title: 'Innovation',
      description: 'Veille technologique constante pour vous proposer les meilleures solutions du marché.'
    },
    {
      icon: '🔒',
      title: 'Fiabilité',
      description: 'Respect des délais, transparence sur les prix, code propre et documenté. Zéro mauvaise surprise.'
    }
  ];

  const stats = [
    { value: '150+', label: 'Projets réalisés' },
    { value: '98%', label: 'Satisfaction client' },
    { value: '5+', label: 'Années d\'expérience' },
    { value: '24/7', label: 'Support disponible' }
  ];

  return (
    <>
      <Helmet>
        <title>À Propos | Anthony Vanheule - Olney Tech Solutions</title>
        <meta name="description" content="Découvrez l'histoire d'Olney Tech Solutions et d'Anthony Vanheule, développeur web passionné basé à Roncq. Notre mission : accompagner les PME du Nord dans leur transformation digitale." />
        <link rel="canonical" href="https://olney.dev/a-propos" />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-[var(--primary)] rounded-full blur-[150px] opacity-10 pointer-events-none"></div>
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[var(--secondary)] rounded-full blur-[150px] opacity-10 pointer-events-none"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Photo / Visual */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              className="order-2 lg:order-1"
            >
              <div className="relative">
                <div className="aspect-square rounded-3xl bg-gradient-to-br from-[var(--primary)]/20 to-[var(--secondary)]/20 overflow-hidden border border-white/10">
                  {/* Placeholder for photo */}
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-center">
                      <span className="text-8xl mb-4 block">👨‍💻</span>
                      <span className="text-gray-400">Anthony Vanheule</span>
                    </div>
                  </div>
                </div>

                {/* Floating badges */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="absolute -top-4 -right-4 glass-panel rounded-2xl p-4 border-[var(--primary)]/30"
                >
                  <div className="text-[var(--primary)] font-bold text-2xl">5+</div>
                  <div className="text-gray-400 text-xs">Années d'exp.</div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="absolute -bottom-4 -left-4 glass-panel rounded-2xl p-4 border-[var(--secondary)]/30"
                >
                  <div className="text-[var(--secondary)] font-bold text-2xl">150+</div>
                  <div className="text-gray-400 text-xs">Projets</div>
                </motion.div>
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              className="order-1 lg:order-2"
            >
              <div className="inline-block mb-4 px-4 py-1 rounded-full border border-[var(--primary)] bg-[rgba(112,0,255,0.1)] text-[var(--primary)] text-sm font-bold tracking-widest uppercase">
                À propos
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 font-['Space_Grotesk'] text-white">
                Bonjour, je suis{' '}
                <span className="text-gradient-electric">Anthony</span>
              </h1>

              <p className="text-xl text-gray-400 mb-8 leading-relaxed">
                Développeur web passionné et fondateur d'Olney Tech Solutions. Depuis 2019, j'accompagne
                les entreprises du Nord de la France dans leur transformation digitale.
              </p>

              <p className="text-gray-400 mb-8 leading-relaxed">
                Basé à Roncq et intervenant également sur Phalempin et toute la métropole lilloise,
                je mets mon expertise au service des PME, artisans et entrepreneurs qui veulent
                se démarquer en ligne.
              </p>

              <div className="flex flex-wrap gap-4 mb-8">
                <a
                  href="https://linkedin.com/in/anthony-vanheule"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-gray-300 hover:text-white hover:border-[var(--primary)] transition-all"
                >
                  <FaLinkedin /> LinkedIn
                </a>
                <a
                  href="https://github.com/olney-tech"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-gray-300 hover:text-white hover:border-[var(--primary)] transition-all"
                >
                  <FaGithub /> GitHub
                </a>
                <a
                  href="mailto:contact@olney.dev"
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-gray-300 hover:text-white hover:border-[var(--primary)] transition-all"
                >
                  <FaEnvelope /> Email
                </a>
                <a
                  href="tel:+33689177385"
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-gray-300 hover:text-white hover:border-[var(--primary)] transition-all"
                >
                  <FaPhone /> Appeler
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 border-y border-[var(--border-glass)] bg-[rgba(255,255,255,0.02)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[var(--primary)] to-[var(--secondary)]">
                  {stat.value}
                </div>
                <div className="text-gray-400 text-sm mt-2">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white font-['Space_Grotesk']">
              Mes <span className="text-gradient-electric">Valeurs</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-panel rounded-3xl p-8 text-center hover:border-[var(--primary)]/50 transition-colors"
              >
                <div className="text-5xl mb-6">{value.icon}</div>
                <h3 className="text-xl font-bold text-white mb-4 font-['Space_Grotesk']">
                  {value.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white/[0.02]">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white font-['Space_Grotesk']">
              Mon <span className="text-gradient-electric">Parcours</span>
            </h2>
          </motion.div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-0 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[var(--primary)] to-[var(--secondary)]"></div>

            <div className="space-y-12">
              {timeline.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative flex items-center ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  {/* Dot */}
                  <div className="absolute left-0 md:left-1/2 w-4 h-4 rounded-full bg-[var(--primary)] transform -translate-x-1/2 border-4 border-[#030014]"></div>

                  {/* Content */}
                  <div className={`ml-8 md:ml-0 md:w-1/2 ${
                    index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'
                  }`}>
                    <div className="glass-panel rounded-2xl p-6">
                      <span className="text-[var(--primary)] font-bold text-sm">{item.year}</span>
                      <h3 className="text-xl font-bold text-white mt-2 font-['Space_Grotesk']">
                        {item.title}
                      </h3>
                      <p className="text-gray-400 mt-2 text-sm">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-panel rounded-3xl p-12 border-[var(--primary)]/30 relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-[var(--primary)] rounded-full blur-[100px] opacity-20"></div>

            <div className="relative z-10">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-8 font-['Space_Grotesk'] text-center">
                Notre <span className="text-gradient-electric">Mission</span>
              </h2>

              <p className="text-xl text-gray-300 text-center leading-relaxed mb-8">
                "Rendre le digital accessible et performant pour toutes les entreprises du Nord,
                quelle que soit leur taille. Chaque projet est une opportunité de créer quelque chose
                d'exceptionnel ensemble."
              </p>

              <div className="flex justify-center">
                <Link
                  to="/contact"
                  className="btn-magnetic px-8 py-4 bg-[var(--primary)] text-white font-bold rounded-xl hover:opacity-90 transition-all"
                >
                  Discutons de votre projet
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white/[0.02]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6 font-['Space_Grotesk']">
              Prêt à travailler ensemble ?
            </h2>
            <p className="text-gray-400 text-lg mb-8">
              Que vous ayez un projet précis ou juste une idée, je suis là pour vous accompagner.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/contact"
                className="btn-magnetic px-8 py-4 bg-[var(--primary)] text-white font-bold rounded-xl"
              >
                Me contacter
              </Link>
              <Link
                to="/portfolio"
                className="px-8 py-4 border border-white/20 text-white font-bold rounded-xl hover:bg-white/5 transition-all"
              >
                Voir mes réalisations
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default About;
